import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { CircularProgress, Typography, Box } from '@mui/material';

const DEFAULT_COLORS = [
  '#003781',
  '#0057A8',
  '#1976D2',
  '#42A5F5',
  '#90CAF9',
  '#1565C0',
];

/**
 * Custom tooltip — white card, rounded, shadow, coloured dots per series.
 * valueFormatter renders the real duration string (the same one the axis and
 * bar labels use) instead of the raw axis-unit float underneath a hover —
 * without it this showed unformatted noise like "16.666666666666668".
 */
const CustomTooltip = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        border: 'none',
        p: 1.5,
        minWidth: 140,
      }}
    >
      <Typography variant="body2" fontWeight={700} mb={0.75} sx={{ color: '#003781' }}>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Box key={entry.dataKey} display="flex" alignItems="center" gap={1} mb={0.25}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: entry.fill || entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            {entry.name}:&nbsp;
            <span style={{ fontWeight: 600, color: '#003781' }}>
              {valueFormatter ? valueFormatter(entry.value) : entry.value}
            </span>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Value label rendered above a bar — plain SVG text so it lives inside the
 * chart's own <svg> (a MUI Typography here would not paint at all). Muted
 * grey, never the bar's own colour -- a label is a value, not an identity
 * mark. Renders nothing for a null/undefined data point (no bar, no label).
 */
const makeValueLabelRenderer = (formatter) => (props) => {
  const { x, y, width, value } = props;
  if (value === null || value === undefined || x == null || y == null || width == null) {
    return null;
  }
  return (
    <text
      x={Number(x) + Number(width) / 2}
      y={Number(y) - 6}
      fill="#6b7280"
      fontSize={10}
      textAnchor="middle"
    >
      {formatter ? formatter(value) : value}
    </text>
  );
};

/**
 * X-axis tick that keeps a long category name UPRIGHT and wraps it onto
 * several lines, instead of rotating it diagonally. Rotated labels are hard to
 * read, and every one of them has to be mentally un-rotated to match a bar.
 * With ~10 categories across a full-width chart there is enough room per label
 * to break on word boundaries and leave the text horizontal.
 *
 * Recharts clones this element per tick and supplies x/y/payload plus the axis
 * `width` and `visibleTicksCount`, which is what gives us the per-label slot.
 */
const WrappedAxisTick = ({ x, y, payload, width, visibleTicksCount, maxLines = 3 }) => {
  const slot = width && visibleTicksCount ? width / visibleTicksCount : 120;
  // ~5.8px per character at fontSize 11 — measured off the existing dashboard
  // labels. Floor of 10 keeps very narrow viewports from breaking every word
  // onto its own line.
  const charsPerLine = Math.max(10, Math.floor((slot - 6) / 5.8));

  const lines = [];
  String(payload?.value ?? '')
    .split(' ')
    .forEach((word) => {
      const last = lines[lines.length - 1];
      if (last !== undefined && `${last} ${word}`.length <= charsPerLine) {
        lines[lines.length - 1] = `${last} ${word}`;
      } else {
        lines.push(word);
      }
    });

  const shown = lines.slice(0, maxLines);
  if (lines.length > maxLines && shown.length > 0) {
    shown[shown.length - 1] = `${shown[shown.length - 1]}…`;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      {shown.map((line, i) => (
        <text
          key={i}
          x={0}
          y={0}
          dy={13 + i * 12}
          textAnchor="middle"
          fill="#6b7280"
          fontSize={11}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

/**
 * Custom legend — rounded colour dots.
 *
 * `order` is the series names in the order the bars are drawn. Recharts' own
 * payload order is not guaranteed to match the Bar child order, and on the
 * stage-cycle-time chart it came out reversed, so the legend read
 * automated/manual/target against bars drawn target/manual/automated -- which
 * silently mislabels every colour. Sorting against the caller's own series list
 * removes the guesswork; it is a no-op wherever the two already agree.
 */
const CustomLegend = ({ payload, order }) => {
  if (!payload || payload.length === 0) return null;
  const entries = order && order.length
    ? [...payload].sort((a, b) => {
        const ia = order.indexOf(a.value);
        const ib = order.indexOf(b.value);
        // Anything absent from `order` keeps its original position at the end
        // rather than jumping to the front on a -1.
        return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
      })
    : payload;
  return (
    <Box display="flex" flexWrap="wrap" gap={1.5} justifyContent="center" mt={0.5}>
      {entries.map((entry) => (
        <Box key={entry.value} display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: '#374151', fontWeight: 500 }}>
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * DashboardBarChart — grouped bar chart using Recharts.
 *
 * Props:
 *   data          — array of objects
 *   xKey          — string key used for the X axis (e.g. 'quarter')
 *   series        — [{ dataKey, name, color }]
 *   title         — string
 *   height        — number (default 300)
 *   loading       — bool
 *   yLabel        — string (default 'Count')
 *   yTickInterval   — number (e.g. 10 → ticks 0,10,20,... up to rounded max)
 *   yTickFormatter  — function (value) => string  (e.g. v => `${v}%`)
 *   xTickWrap       — bool — for long category names: keeps the tick labels
 *                      horizontal and wraps them over up to 3 lines, rather
 *                      than letting them collide. Also moves the legend to the
 *                      top, since a bottom legend sits in the same space as
 *                      the taller multi-line labels.
 *   yDomain         — explicit [min, max] — overrides Recharts' own auto-scale.
 *                      Use when the series can span very different magnitudes
 *                      across categories (e.g. a few seconds next to several
 *                      days) and letting Recharts guess produces a scale that
 *                      doesn't match the actual data.
 *   minPointSize    — passed straight to each <Bar> — gives a real-but-tiny
 *                      value a minimum visible height instead of a sub-pixel
 *                      (invisible) bar. Never applies to null/undefined --
 *                      those still render no bar at all.
 *   showValueLabels — draws the formatted value above each bar, for exactly
 *                      the case minPointSize exists for: a bar too short to
 *                      read by height alone.
 *   valueLabelFormatter — function (value) => string, used by showValueLabels
 *                      (falls back to yTickFormatter, then the raw value)
 */
const DashboardBarChart = ({
  data = [],
  xKey = 'quarter',
  series = [],
  title = '',
  height = 300,
  loading = false,
  yLabel = 'Count',
  yTickInterval = null,
  yTickFormatter = null,
  xTickWrap = false,
  yDomain = null,
  minPointSize = 0,
  showValueLabels = false,
  valueLabelFormatter = null,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  // Build explicit Y-axis ticks when yTickInterval is set. yDomain (an
  // explicit override) takes priority over both this and Recharts' own
  // auto-scale -- callers pass it when the auto-scale can't be trusted (see
  // prop doc above).
  let yAxisProps = {};
  if (yDomain) {
    yAxisProps = { domain: yDomain };
  } else if (yTickInterval) {
    const allValues = data.flatMap((row) =>
      series.map((s) => Number(row[s.dataKey]) || 0)
    );
    const maxVal = Math.max(0, ...allValues);
    const ceiling = Math.ceil(maxVal / yTickInterval) * yTickInterval || yTickInterval;
    const ticks = [];
    for (let t = 0; t <= ceiling; t += yTickInterval) ticks.push(t);
    yAxisProps = { ticks, domain: [0, ceiling] };
  }

  const valueLabelRenderer = showValueLabels
    ? makeValueLabelRenderer(valueLabelFormatter || yTickFormatter)
    : null;

  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle2"
          fontWeight={700}
          mb={1.5}
          sx={{ color: '#374151' }}
        >
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: xTickWrap ? 44 : 8, right: 16, left: 0, bottom: 8 }}
        >
          <CartesianGrid stroke="#e8eef7" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={xTickWrap ? <WrappedAxisTick /> : { fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            interval={0}
            height={xTickWrap ? 58 : 30}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={yTickFormatter || undefined}
            allowDataOverflow={!!yDomain}
            {...yAxisProps}
          />
          <Tooltip
            content={<CustomTooltip valueFormatter={valueLabelFormatter || yTickFormatter || undefined} />}
            cursor={{ fill: 'rgba(0,55,129,0.05)' }}
          />
          {/* A custom Legend content does not grow Recharts' own layout
              reservation, so at the bottom it lands on top of the taller
              multi-line X labels -- put it above the plot instead. */}
          <Legend
            content={<CustomLegend order={series.map((s) => s.name)} />}
            verticalAlign={xTickWrap ? 'top' : 'bottom'}
          />
          {series.map((s, idx) => (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name || s.dataKey}
              fill={s.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
              radius={[4, 4, 0, 0]}
              minPointSize={minPointSize}
              maxBarSize={48}
            >
              {valueLabelRenderer && <LabelList dataKey={s.dataKey} content={valueLabelRenderer} />}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default DashboardBarChart;
